/**
 * Page-size presets for the editor's page-setup control.
 *
 * The CDM's own `PageSize` enum only names Letter, Legal, A4 and Custom, because that is all the
 * DOCX parser can round-trip as a named size. Everything else here — Tabloid, A3, A5, Executive and
 * the slide sizes — is `Custom` plus explicit dimensions, which is exactly how Word itself stores a
 * non-standard page. So this is a UI convenience over the existing model: no schema change, and a
 * document authored from a preset round-trips through every renderer unchanged.
 *
 * The slide presets are worth having specifically because the PPTX renderer derives its slide size
 * from `section.Page` whenever the setup is landscape or Custom. Picking "Slide · 16:9" here is
 * therefore the way to get a correctly-proportioned deck out the other end; before this the only
 * route was typing 960 × 540 into the width and height boxes and knowing why.
 */
import { PageSize, Orientation, type PageSetup } from '../../cdm/types'

export type PresetKind = 'paper' | 'slide'

export interface PagePreset {
  id: string
  label: string
  kind: PresetKind
  /** The CDM size enum to store. Anything the enum cannot name is Custom. */
  size: PageSize
  /** Dimensions in the preset's NATURAL orientation — portrait for paper, landscape for slides. */
  widthPt: number
  heightPt: number
}

/** 1 inch = 72pt; 1 mm = 72/25.4 pt. Values are rounded to whole points, as Word stores them. */
export const PAGE_PRESETS: PagePreset[] = [
  // ── Paper, portrait-native ──────────────────────────────────────────────
  { id: 'letter',    label: 'Letter (8.5 × 11 in)',     kind: 'paper', size: PageSize.Letter, widthPt: 612, heightPt: 792 },
  { id: 'legal',     label: 'Legal (8.5 × 14 in)',      kind: 'paper', size: PageSize.Legal,  widthPt: 612, heightPt: 1008 },
  { id: 'tabloid',   label: 'Tabloid (11 × 17 in)',     kind: 'paper', size: PageSize.Custom, widthPt: 792, heightPt: 1224 },
  { id: 'executive', label: 'Executive (7.25 × 10.5 in)', kind: 'paper', size: PageSize.Custom, widthPt: 522, heightPt: 756 },
  { id: 'a3',        label: 'A3 (297 × 420 mm)',        kind: 'paper', size: PageSize.Custom, widthPt: 842, heightPt: 1191 },
  { id: 'a4',        label: 'A4 (210 × 297 mm)',        kind: 'paper', size: PageSize.A4,     widthPt: 595, heightPt: 842 },
  { id: 'a5',        label: 'A5 (148 × 210 mm)',        kind: 'paper', size: PageSize.Custom, widthPt: 420, heightPt: 595 },

  // ── Slides, landscape-native ────────────────────────────────────────────
  // PowerPoint's own defaults: 16:9 is 13.333 × 7.5 in (12192000 × 6858000 EMU).
  { id: 'slide-16-9',  label: 'Slide · 16:9 widescreen (13.33 × 7.5 in)', kind: 'slide', size: PageSize.Custom, widthPt: 960, heightPt: 540 },
  { id: 'slide-4-3',   label: 'Slide · 4:3 standard (10 × 7.5 in)',       kind: 'slide', size: PageSize.Custom, widthPt: 720, heightPt: 540 },
  { id: 'slide-16-10', label: 'Slide · 16:10 (10 × 6.25 in)',             kind: 'slide', size: PageSize.Custom, widthPt: 720, heightPt: 450 },
]

/** Custom is not a preset — it is the absence of one — so it gets its own sentinel id. */
export const CUSTOM_PRESET_ID = 'custom'

/** A point of slack, because a round-tripped document can come back a hair off an exact figure. */
const near = (a: number, b: number) => Math.abs(a - b) <= 1

/**
 * The preset a page setup currently corresponds to, or null for a genuinely custom size.
 *
 * Paper matches as an UNORDERED pair, so Letter portrait and Letter landscape both resolve to
 * Letter — the dropdown names the paper and the orientation buttons beside it name the rest.
 *
 * A slide matches only in its own orientation. There is no such thing as a portrait 16:9 slide, so
 * rotating one produces a genuinely custom size and the dropdown says so, rather than keeping a
 * label that would now be describing something that does not exist.
 */
export function matchPagePreset(page: Pick<PageSetup, 'widthPt' | 'heightPt'>): PagePreset | null {
  return PAGE_PRESETS.find(p => {
    const asIs = near(p.widthPt, page.widthPt) && near(p.heightPt, page.heightPt)
    if (p.kind === 'slide') return asIs
    const rotated = near(p.heightPt, page.widthPt) && near(p.widthPt, page.heightPt)
    return asIs || rotated
  }) ?? null
}

/**
 * The patch that applies a preset, given the orientation currently selected.
 *
 * Paper keeps whatever orientation the user already chose. A slide does not: there is no such thing
 * as a portrait 16:9 slide, and silently producing a 540 × 960 "slide" would be worse than ignoring
 * the button — so choosing a slide preset sets landscape, and the orientation control follows.
 */
export function applyPagePreset(preset: PagePreset, currentOrientation: Orientation): Partial<PageSetup> {
  const orientation = preset.kind === 'slide' ? Orientation.Landscape : currentOrientation

  // Swap only when the target orientation differs from the preset's OWN. Paper is stored portrait,
  // slides landscape, so assuming a single native orientation here silently produced a 540 × 960
  // "slide" — the exact outcome the paragraph above says must not happen.
  const nativeIsLandscape = preset.kind === 'slide'
  const wantLandscape = orientation === Orientation.Landscape
  const swap = wantLandscape !== nativeIsLandscape

  return {
    size: preset.size,
    orientation,
    widthPt: swap ? preset.heightPt : preset.widthPt,
    heightPt: swap ? preset.widthPt : preset.heightPt,
  }
}
